// LA VUELTA DEL ENLACE DE PAGO QUE MANDA EL EQUIPO (11-10-2026).
//
// Quien paga ese enlace vuelve a `/planes?pago=ok&session_id=cs_...`. Hasta hoy la app no
// hacía nada con ese id: el pago solo quedaba apuntado si llegaba el aviso de Stripe, y en
// dev no llega nunca (un pago de 897 € de prueba se cobró y la app no se enteró). Ahora se
// le manda el id al servidor, que le pregunta a Stripe y lo apunta igual que con el aviso
// (`POST /billing/enlace-de-lead/confirmar`, que contesta solo «apuntado» sí o no).
//
// SIN SESIÓN, A PROPÓSITO: el que paga puede no tener cuenta todavía, y `/planes` está
// detrás del login. Por eso lo lanza la app al cargar (`VueltaDelEnlace` en App.js), antes
// de que nada le mande a identificarse, y la pantalla de planes pregunta por el MISMO
// resultado: se guarda por sesión de Stripe, así que el doble montaje de React y las dos
// llamadas hacen una sola petición.
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// El id de la sesión de Stripe si esta dirección es la vuelta de un enlace pagado; si no, null.
// `marca` es el parámetro que dice que es una vuelta: `pago` en el enlace del lead,
// `renovado` en la renovación y `checkout` en la compra de un plan (más abajo). Todas valen
// `ok` menos la de la compra, que desde siempre vuelve con `checkout=success`.
const VALOR_DE_LA_MARCA = { checkout: 'success' };
export const sesionDeLaVuelta = (search, marca = 'pago') => {
    const params = new URLSearchParams(search || '');
    if (params.get(marca) !== (VALOR_DE_LA_MARCA[marca] || 'ok')) return null;
    const id = (params.get('session_id') || '').trim();
    return id.startsWith('cs_') ? id : null;
};

// ¿Es la vuelta de un enlace del alta, la que puede entrar sola (lleva el secreto `entrada`)?
// Mientras se confirma, la app no pinta ninguna pantalla debajo (App.js, `VueltaDelEnlace`):
// se veía el login, luego Inicio y luego el cuestionario (Francisco, 16-10).
export const puedeEntrarSola = (search) =>
    !!sesionDeLaVuelta(search) && !!(new URLSearchParams(search || '').get('entrada') || '').trim();

const pedidas = new Map();

// La respuesta del servidor a esta vuelta, una vez por sesión de Stripe (y por si lleva el
// secreto de entrar). Nunca rechaza: si algo falla es `{}`, el detalle va a la consola y la
// pantalla se queda con su frase prudente.
const respuestaDeLaVuelta = (search, post, conEntrada) => {
    const id = sesionDeLaVuelta(search);
    if (!id) return Promise.resolve({});
    const entrada = conEntrada ? (new URLSearchParams(search || '').get('entrada') || '').trim() : '';
    const clave = entrada ? `${id}|entrada` : id;
    if (!pedidas.has(clave)) {
        pedidas.set(clave, Promise.resolve()
            .then(() => post(`${API}/api/billing/enlace-de-lead/confirmar`,
                entrada ? { session_id: id, entrada } : { session_id: id }))
            .then((r) => r?.data || {})
            .catch((e) => { console.error('[vuelta del enlace]', e); return {}; }));
    }
    return pedidas.get(clave);
};

// ¿Ha quedado apuntado el pago de esta vuelta? Sin el secreto de entrar: la pantalla de
// planes la abre también quien tiene otra cuenta abierta, y no debe gastarle la entrada.
export const confirmarVueltaDelEnlace = (search, post = axios.post) =>
    respuestaDeLaVuelta(search, post, false).then((d) => d.apuntado === true);

// LA VUELTA DE QUIEN NO TIENE SESIÓN (16-10): `{ apuntado, sesion }`, con `sesion` =
// `{ access_token, user }` si el servidor le deja entrar ya (solo la cuenta que nació de ese
// pago, con el secreto de la dirección de vuelta, una vez y poco después del cobro:
// `core/alta_al_pagar.cuenta_para_entrar`), o null.
export const vueltaSinSesion = (search, post = axios.post) =>
    respuestaDeLaVuelta(search, post, true).then((d) => ({
        apuntado: d.apuntado === true,
        sesion: d.apuntado === true && d.access_token && d.user ? { access_token: d.access_token, user: d.user } : null,
    }));

// Lo que se le dice al volver. Confirmado se puede decir la verdad; sin confirmar no se
// afirma que se haya cobrado (esta dirección la puede abrir cualquiera).
export const FRASE_PAGO_RECIBIDO = 'Pago recibido. Tu plan se activa en unos segundos; si hay que revisar algo, te escribimos.';
export const FRASE_SI_ACABAS_DE_PAGAR = 'Si acabas de pagar, tu plan se activa en unos segundos. Si hay que revisar algo, te escribimos.';
// A quien vuelve sin sesión no se le promete un plan en segundos: puede no tener ni cuenta.
//
// Y SE LE DICE QUÉ HACER AHORA (14-10, auditoría QA, PAG-09). Acababa de pagar 197 € desde
// WhatsApp, caía en la pantalla de entrar y el único rastro era un aviso de 6 segundos que
// decía "Pago recibido" (y a veces ni eso: la confirmación tarda y el aviso ya se había ido).
// Ahora el aviso dice que el pago llegó y que entre con su correo, y la app lo deja puesto
// hasta que lo cierra o entra (App.js, `VueltaDelEnlace`).
export const FRASE_PAGO_RECIBIDO_SIN_SESION = 'Hemos recibido tu pago. Entra con tu correo para ver tu plan; si aún no tienes cuenta, te escribimos para dártela.';
// Sin confirmar no se afirma que se haya cobrado, pero tampoco se le deja sin una palabra.
export const FRASE_SI_ACABAS_DE_PAGAR_SIN_SESION = 'Si acabas de pagar, entra con tu correo para ver tu plan. Si hay que revisar algo, te escribimos.';

// El aviso de quien vuelve del enlace SIN sesión: `{ frase, confirmado }`, o null si esta
// dirección no es la vuelta de un enlace (entonces no se dice nada).
export const avisoDeVueltaSinSesion = (search, apuntado) => {
    if (!sesionDeLaVuelta(search)) return null;
    return apuntado === true
        ? { frase: FRASE_PAGO_RECIBIDO_SIN_SESION, confirmado: true }
        : { frase: FRASE_SI_ACABAS_DE_PAGAR_SIN_SESION, confirmado: false };
};


// LA VUELTA DE LA RENOVACIÓN (12-10-2026): `/renovacion?renovado=ok&salida=...&session_id=cs_...`.
//
// La pantalla decía "¡Pago confirmado! Tu plan nuevo ya está en marcha." con solo ver
// `renovado=ok` en la dirección: sin `session_id` se saltaba la comprobación, y con el de un
// pago abandonado el servidor contestaba 200 con `payment_status: unpaid` y nadie lo miraba.
// El plan no se activaba; el mensaje era falso. Ahora solo es "pagado" lo que el servidor
// dice que Stripe da por pagado.
//
// Es la misma idea que la vuelta del enlace de arriba (el id de la sesión y UNA petición por
// sesión, que el efecto de la pantalla se monta dos veces), pero con la sesión del cliente:
// `post` es el de `api` (`api.post`), que lleva su identificación.
export const PAGADO = 'pagado';
export const SIN_PAGAR = 'sin_pagar';

const renovaciones = new Map();

// Lo que el servidor cuenta de la sesión de Stripe: pagada es `paid`. Una suscripción que
// arranca sin cobro ese día (Stripe dice `no_payment_required`) vale si la suscripción ha
// quedado viva; una sesión `unpaid`, nunca.
const estaPagada = (d) => d?.payment_status === 'paid'
    || (d?.payment_status === 'no_payment_required' && ['active', 'trialing'].includes(d?.subscription_status));

// 'pagado' o 'sin_pagar'. Nunca rechaza: si la petición falla es 'sin_pagar' y el detalle va
// a la consola (recargar lo reintenta: un fallo no se queda guardado).
//
// LA COMPRA DE UN PLAN VUELVE POR LA MISMA PUERTA (12-10): `/planes?checkout=success&session_id=...`
// decía "¡Pago confirmado! Tu plan está activo" igual que lo hacía la renovación, sin mirar
// la respuesta y aunque no hubiera `session_id`. Es la misma pregunta al mismo servidor, así
// que la pantalla de planes llama aquí con `marca = 'checkout'`.
export const confirmarVueltaDeRenovacion = (search, post, marca = 'renovado') => {
    const id = sesionDeLaVuelta(search, marca);
    if (!id) return Promise.resolve(SIN_PAGAR);
    if (!renovaciones.has(id)) {
        renovaciones.set(id, Promise.resolve()
            .then(() => post('/billing/checkout-session/sync', { session_id: id }))
            .then((r) => (estaPagada(r?.data) ? PAGADO : SIN_PAGAR))
            .catch((e) => {
                console.error('[vuelta de la renovación]', e);
                renovaciones.delete(id);
                return SIN_PAGAR;
            }));
    }
    return renovaciones.get(id);
};

// Lo que se le dice al volver, según la salida que eligió. Las tres usan la misma dirección
// de vuelta, y al que se baja a Mantenimiento no se le dice que su plan nuevo está en marcha.
export const fraseDePagoConfirmado = (salida) => ({
    salida: '¡Pago confirmado! Ya estás en Mantenimiento.',
    cambiar: '¡Pago confirmado! Ya estás en tu plan nuevo.',
}[salida] || '¡Pago confirmado! Tu plan nuevo ya está en marcha.');
export const FRASE_PAGO_SIN_CONFIRMAR = 'No hemos podido confirmar el pago. Si te han cobrado, recarga en unos segundos.';
